import { useState } from "react";
import { X, Quote, MapPin } from "lucide-react";
import { COMPLETED_PROJECTS } from "../data/site";
import { useReveal } from "../hooks/useReveal";
import FlyWords from "./FlyWords";

export default function Gallery() {
  const [active, setActive] = useState(null);
  const ref = useReveal();

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="section bg-[#0a0a0a] relative grain"
      ref={ref}
    >
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 reveal">
          <div>
            <div className="overline mb-5">Completed projects</div>
            <FlyWords
              as="h2"
              className="font-display text-4xl md:text-5xl lg:text-6xl text-white font-light leading-tight"
              segments={[{ text: "Recent installs, real reviews." }]}
            />
          </div>
          <p className="text-[#A1A1AA] max-w-md text-base md:text-lg">
            Every wall below was designed, built and installed by Swift Luxury Walls — with the homeowner's own
            words underneath each photo.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {COMPLETED_PROJECTS.map((p, i) => (
            <article
              key={p.id}
              data-testid={`project-${p.id}`}
              className="reveal group rounded-2xl border border-[#27272A] bg-[#0d0d0d] overflow-hidden hover:border-[#AA8035] transition-colors"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <button
                type="button"
                data-testid={`project-image-${p.id}`}
                onClick={() => setActive(p)}
                className="block w-full text-left"
                aria-label={`View ${p.title} full-size`}
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#111]">
                  <img
                    src={p.src}
                    alt={`${p.title} — installed by Swift Luxury Walls in ${p.city}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.03]"
                  />
                </div>
              </button>

              <div className="p-6 md:p-8">
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <div>
                    <div className="font-display text-2xl md:text-3xl text-white leading-tight">{p.title}</div>
                    <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mt-2 inline-flex items-center gap-1.5">
                      <MapPin className="w-3 h-3" /> {p.city}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-[#A1A1AA] mt-3 leading-relaxed">{p.materials}</div>

                <div className="mt-6 pt-6 border-t border-[#1a1a1a] relative">
                  <Quote className="absolute -top-3 left-0 w-6 h-6 text-[#D4AF37]/70 bg-[#0d0d0d] px-1" />
                  <p
                    data-testid={`project-quote-${p.id}`}
                    className="text-sm md:text-[15px] text-[#D1D1D6] leading-relaxed italic pl-1"
                  >
                    “{p.quote}”
                  </p>
                  <div className="mt-4 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full border border-[#D4AF37]/40 bg-[#111] flex items-center justify-center text-[#D4AF37] font-display">
                      {p.client.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm text-white">{p.client}</div>
                      <div className="text-[10px] tracking-[0.22em] uppercase text-[#6b6b72]">{p.role}</div>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      {active && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setActive(null)}
        >
          <button
            data-testid="lightbox-close"
            className="absolute top-5 right-5 p-2 text-white/80 hover:text-[#D4AF37]"
            onClick={() => setActive(null)}
            aria-label="Close"
          >
            <X className="h-7 w-7" />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={active.src}
              alt={active.title}
              className="w-full max-h-[82vh] object-contain rounded-lg"
            />
            <div className="text-center mt-4">
              <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37]">
                {active.city}
              </div>
              <div className="font-display text-2xl text-white mt-1">{active.title}</div>
              <p className="text-sm text-[#A1A1AA] italic max-w-2xl mx-auto mt-3">
                “{active.quote}” — {active.client}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
